<template>
  <div class="circular-countdown" :style="countdownStyle">
    <div v-if="countdownSettings.showDays" class="countdown-block">
      <svg class="countdown-svg">
        <circle :r="radius" cx="50" cy="50" class="bg-circle"></circle>
        <circle :r="radius" cx="50" cy="50" class="progress-circle" :style="{ strokeDashoffset: daysStrokeOffset }"></circle>
      </svg>
      <div class="countdown-text">
        <span class="countdown-digit">{{ displayDays }}</span>
        <span class="countdown-label">Days</span>
      </div>
    </div>
    <div v-if="countdownSettings.showHours" class="countdown-block">
      <svg class="countdown-svg">
        <circle :r="radius" cx="50" cy="50" class="bg-circle"></circle>
        <circle :r="radius" cx="50" cy="50" class="progress-circle" :style="{ strokeDashoffset: hoursStrokeOffset }"></circle>
      </svg>
      <div class="countdown-text">
        <span class="countdown-digit">{{ displayHours }}</span>
        <span class="countdown-label">Hours</span>
      </div>
    </div>
    <div v-if="countdownSettings.showMinutes" class="countdown-block">
      <svg class="countdown-svg">
        <circle :r="radius" cx="50" cy="50" class="bg-circle"></circle>
        <circle :r="radius" cx="50" cy="50" class="progress-circle" :style="{ strokeDashoffset: minutesStrokeOffset }"></circle>
      </svg>
      <div class="countdown-text">
        <span class="countdown-digit">{{ displayMinutes }}</span>
        <span class="countdown-label">Minutes</span>
      </div>
    </div>
    <div v-if="countdownSettings.showSeconds" class="countdown-block">
      <svg class="countdown-svg">
        <circle :r="radius" cx="50" cy="50" class="bg-circle"></circle>
        <circle :r="radius" cx="50" cy="50" class="progress-circle" :style="{ strokeDashoffset: secondsStrokeOffset }"></circle>
      </svg>
      <div class="countdown-text">
        <span class="countdown-digit">{{ displaySeconds }}</span>
        <span class="countdown-label">Seconds</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CircularCountdown',
  props: ['endDate', 'countdownSettings'],
  data() {
    return {
      displayDays: '0',
      displayHours: '0',
      displayMinutes: '0',
      displaySeconds: '0',
      radius: 45,
      circumference: 2 * Math.PI * 45,
      timer: null
    };
  },
  computed: {
    countdownStyle() {
      return {
        '--text-color': this.countdownSettings.textColour || '#FFFFFF',
        '--background-color': this.countdownSettings.backgroundColour || '#1E1E1E',
      };
    },
    daysStrokeOffset() {
      // Assuming a max of 365 days for visualization. Adjust if needed.
      return this.circumference * (1 - (this.displayDays / 365));
    },
    hoursStrokeOffset() {
      return this.circumference * (1 - (this.displayHours / 24));
    },
    minutesStrokeOffset() {
      return this.circumference * (1 - (this.displayMinutes / 60));
    },
    secondsStrokeOffset() {
      return this.circumference * (1 - (this.displaySeconds / 60));
    }
  },
  mounted() {
    this.updateTime();
    this.timer = setInterval(this.updateTime, 1000);
  },
  beforeUnmount() {
    clearInterval(this.timer);
  },
  methods: {
    updateTime() {
      const remaining = new Date(this.endDate).getTime() - new Date().getTime();
      if (remaining < 0) {
        clearInterval(this.timer);
        this.displayDays = '0';
        this.displayHours = '0';
        this.displayMinutes = '0';
        this.displaySeconds = '0';
        return;
      }
      this.displayDays = Math.floor(remaining / (1000 * 60 * 60 * 24));
      this.displayHours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      this.displayMinutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      this.displaySeconds = Math.floor((remaining % (1000 * 60)) / 1000);
    }
  }
}
</script>

<style scoped>
.circular-countdown {
  display: flex;
  justify-content: center;
  gap: 30px;
  padding: 20px;
  background-color: var(--background-color);
  font-family: 'Roboto', sans-serif;
  flex-wrap: wrap;
}

.countdown-block {
  position: relative;
  width: 100px;
  height: 100px;
  text-align: center;
}

.countdown-svg {
  width: 100px;
  height: 100px;
  transform: rotate(-90deg);
}

.bg-circle {
  fill: none;
  stroke: rgba(255, 255, 255, 0.1);
  stroke-width: 10;
}

.progress-circle {
  fill: none;
  stroke: var(--text-color);
  stroke-width: 10;
  stroke-linecap: round;
  transition: stroke-dashoffset 1s linear;
}

.countdown-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--text-color);
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.countdown-digit {
  font-size: 2rem;
  font-weight: bold;
}

.countdown-label {
  font-size: 0.8rem;
  text-transform: uppercase;
}
</style>